import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, Calendar, RefreshCw, ArrowRight, User } from "lucide-react";
import Seo, { buildBreadcrumbLd, buildFaqLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import CtaSection from "../components/CtaSection";
import { getArticle } from "../data/articles";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/ratgeber" replace />;

  const crumbs = [
    { label: "Startseite", to: "/" },
    { label: "Ratgeber", to: "/ratgeber" },
    { label: article.title, to: `/ratgeber/${article.slug}` },
  ];

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.metaDescription,
    datePublished: article.published,
    dateModified: article.updated,
    author: { "@type": "Organization", name: "Rümpeltaxi" },
    publisher: { "@type": "Organization", name: "Rümpeltaxi" },
  };

  const related = (article.related || []).map(getArticle).filter(Boolean);

  return (
    <div data-testid={`page-article-${article.slug}`}>
      <Seo
        path={`/ratgeber/${article.slug}`}
        title={article.metaTitle}
        description={article.metaDescription}
        jsonLd={[buildBreadcrumbLd(crumbs), articleLd, buildFaqLd(article.faq)]}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero kicker={article.category} title={article.title} lead={article.excerpt}>
        <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-light-gray/60">
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {article.readingTime}</span>
          <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> Veröffentlicht {article.published}</span>
          <span className="flex items-center gap-1.5"><RefreshCw className="h-4 w-4" /> Aktualisiert {article.updated}</span>
        </div>
      </PageHero>

      <section className="bg-white py-16 md:py-24">
        <div className="container-page grid gap-12 lg:grid-cols-[0.32fr_0.68fr] lg:gap-16">
          {/* TOC */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-sm border border-light-gray bg-light-gray p-6">
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">
                Inhaltsverzeichnis
              </h2>
              <nav className="mt-4 space-y-2" data-testid="article-toc">
                {article.sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block text-sm text-anthracite transition-colors hover:text-yellow-dark"
                  >
                    {s.heading}
                  </a>
                ))}
              </nav>
            </div>
            <div className="mt-4 flex items-center gap-3 rounded-sm border border-light-gray p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-light-gray">
                <User className="h-5 w-5 text-mid-gray" />
              </span>
              <div>
                <p className="font-heading text-sm font-bold text-anthracite">Rümpeltaxi Redaktion</p>
                <p className="text-xs text-mid-gray">[Autorenprofil folgt]</p>
              </div>
            </div>
          </aside>

          {/* Content */}
          <article className="max-w-2xl">
            {article.sections.map((s) => (
              <Reveal key={s.id}>
                <div id={s.id} className="mb-10 scroll-mt-28">
                  <h2 className="font-heading text-2xl font-bold text-anthracite md:text-3xl">{s.heading}</h2>
                  {s.paragraphs.map((p, i) => (
                    <p key={i} className="mt-4 text-base leading-relaxed text-mid-gray md:text-lg">{p}</p>
                  ))}
                </div>
              </Reveal>
            ))}

            {article.faq?.length > 0 && (
              <div className="mt-4 border-t border-light-gray pt-8">
                <h2 className="font-heading text-2xl font-bold text-anthracite">Häufige Fragen</h2>
                <div className="mt-4">
                  <FaqAccordion items={article.faq} idPrefix={article.slug} />
                </div>
              </div>
            )}
          </article>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-light-gray py-16">
          <div className="container-page">
            <h2 className="font-heading text-xl font-bold text-anthracite">Weiterlesen</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/ratgeber/${r.slug}`}
                  className="group flex items-center justify-between rounded-sm border border-white bg-white p-5 transition-colors hover:border-yellow"
                  data-testid={`article-related-${r.slug}`}
                >
                  <span className="font-heading font-bold text-anthracite">{r.title}</span>
                  <ArrowRight className="h-5 w-5 text-mid-gray transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaSection />
    </div>
  );
}
